import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Navbar from '../dashboard/components/Navbar';
import Sidebar from '../dashboard/components/Sidebar';
import { useToast } from '../../components/base/ToastContainer';
import { requisicionesService } from '../../services/requisiciones.service';
import { cotizacionesService } from '../../services/cotizaciones.service';

interface CotizacionItem {
    id: string;
    medicamentoId?: string;
    producto: string;
    cantidad: number;
    precioUnitario: number;
    iva: number;
    subtotal: number;
    total: number;
    // UI states for search
    searchResults?: any[];
    showResults?: boolean;
    searching?: boolean;
}

export default function CotizacionFormPage() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const isEditing = !!id;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        numero: 'COT-',
        fecha: new Date().toISOString().split('T')[0],
        validezDias: 30,
        observaciones: '',
        requisicionId: '', // If coming from requisition
        requisicionNumero: ''
    });

    const [items, setItems] = useState<CotizacionItem[]>([]);

    useEffect(() => {
        // Check if we have state passed from navigation (e.g. from Requisition Selection)
        if (location.state?.requisicion) {
            const req = location.state.requisicion;
            setFormData(prev => ({
                ...prev,
                requisicionId: req.id,
                requisicionNumero: req.numero,
                observaciones: `Cotización basada en Requisición ${req.numero}`
            }));
            loadRequisitionItems(req.id);
        }
    }, [location.state]);

    const loadRequisitionItems = async (reqId: string) => {
        try {
            setLoading(true);
            const details = await requisicionesService.getDetalle(reqId);
            const newItems = details.map((det: any) => ({
                id: Date.now() + Math.random().toString(),
                medicamentoId: det.medicamento_id,
                producto: det.medicamento,
                cantidad: det.cantidad,
                precioUnitario: 0,
                iva: 0,
                subtotal: 0,
                total: 0,
                searchResults: [],
                showResults: false,
                searching: false
            }));

            setItems(newItems);
        } catch (error) {
            console.error('Error loading requisition details:', error);
            showToast('Error al cargar items de la requisición', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Search function for a specific item
    const searchMedicamentos = async (itemId: string, query: string) => {
        if (query.length < 3) {
            setItems(prev => prev.map(item =>
                item.id === itemId ? { ...item, searchResults: [], showResults: false } : item
            ));
            return;
        }

        setItems(prev => prev.map(item =>
            item.id === itemId ? { ...item, searching: true, showResults: true } : item
        ));

        try {
            const results = await requisicionesService.getPrincipiosActivos(query);
            setItems(prev => prev.map(item =>
                item.id === itemId ? { ...item, searchResults: results, searching: false } : item
            ));
        } catch (error) {
            console.error(error);
            setItems(prev => prev.map(item =>
                item.id === itemId ? { ...item, searching: false } : item
            ));
        }
    };

    const handleSelectProduct = (itemId: string, medicamento: any) => {
        setItems(prev => prev.map(item => {
            if (item.id === itemId) {
                return {
                    ...item,
                    medicamentoId: medicamento.id_sustancia || medicamento.id,
                    producto: medicamento.nombre_sustancia || medicamento.nombre,
                    precioUnitario: 0,
                    iva: 0,
                    subtotal: 0,
                    total: 0,
                    showResults: false,
                    searchResults: []
                };
            }
            return item;
        }));
    };

    const calculateItemTotals = (cantidad: number, precio: number, iva: number) => {
        const subtotal = cantidad * precio;
        const totalIva = subtotal * (iva / 100);
        return {
            subtotal,
            total: subtotal + totalIva
        };
    };

    const handleAddItemManual = () => {
        setItems(prev => [
            ...prev,
            {
                id: Date.now().toString(),
                producto: '',
                cantidad: 1,
                precioUnitario: 0,
                iva: 0,
                subtotal: 0,
                total: 0,
                searchResults: [],
                showResults: false,
                searching: false
            }
        ]);
    };

    const handleImportFromExcel = (importedItems: any[]) => {
        const newItems = importedItems.map((imp, idx) => {
            const qty = imp.cantidad || 1;
            const price = imp.precioUnitario || 0;
            const iva = imp.iva || 0;
            const { subtotal, total } = calculateItemTotals(qty, price, iva);

            return {
                id: `imported-${Date.now()}-${idx}`,
                medicamentoId: imp.medicamentoId || undefined,
                producto: imp.producto || imp.medicamentoStr || '',
                // Save laboratory context to product string if needed, or if we want to expand CotizacionItem
                // In a full implementation, laboratory might be part of the request.
                cantidad: qty,
                precioUnitario: price,
                iva: iva,
                subtotal: subtotal,
                total: total,
                searchResults: [],
                showResults: false,
                searching: false
            };
        });

        setItems(prev => [...prev, ...newItems]);
        showToast(`Se importaron ${importedItems.length} items de forma exitosa`, 'success');
    };

    const handleItemChange = (id: string, field: keyof CotizacionItem, value: any) => {
        setItems(prevItems => prevItems.map(item => {
            if (item.id === id) {
                const newItem = { ...item, [field]: value };

                if (field === 'producto') {
                    // Trigger search
                    searchMedicamentos(id, value as string);
                }

                if (field === 'cantidad' || field === 'precioUnitario' || field === 'iva') {
                    const { subtotal, total } = calculateItemTotals(
                        field === 'cantidad' ? Number(value) : item.cantidad,
                        field === 'precioUnitario' ? Number(value) : item.precioUnitario,
                        field === 'iva' ? Number(value) : item.iva
                    );
                    newItem.subtotal = subtotal;
                    newItem.total = total;
                }
                return newItem;
            }
            return item;
        }));
    };

    const handleRemoveItem = (id: string) => {
        setItems(items.filter(item => item.id !== id));
    };

    const getUser = () => {
        try {
            const userStored = localStorage.getItem('user');
            if (userStored) {
                return JSON.parse(userStored);
            }
        } catch (e) {
            console.error("Error parsing user from localStorage", e);
        }
        return null;
    };

    const getBodega = () => {
        try {
            const bodegaStored = localStorage.getItem('selectedBodega');
            if (bodegaStored) {
                return JSON.parse(bodegaStored);
            }
            const userStored = localStorage.getItem('user');
            if (userStored) {
                const user = JSON.parse(userStored);
                if (user.bodegas && user.bodegas.length > 0) {
                    return user.bodegas[0];
                }
            }
        } catch (e) {
            console.error("Error parsing bodega from localStorage", e);
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const user = getUser();
        const bodega = getBodega();

        if (!user || !user.id) {
            showToast('Error: No se ha identificado el usuario', 'error');
            return;
        }

        if (!bodega || !bodega.idbodega) {
            showToast('Error: No se ha seleccionado una bodega', 'error');
            return;
        }

        if (items.length === 0) {
            showToast('Debe agregar al menos un item', 'error');
            return;
        }

        const payload = {
            id_cotizacion: id || null,
            idbodega: bodega.idbodega,
            fecha: formData.fecha,
            requisicion_id: formData.requisicionId || null,
            validez_dias: formData.validezDias,
            total: totalGeneral,
            observaciones: formData.observaciones,
            user_crea: user.id,
            items: items.map(item => ({
                medicamento_id: item.medicamentoId,
                cantidad: item.cantidad,
                precio_unitario: item.precioUnitario,
                subtotal: item.subtotal
            }))
        };

        try {
            setLoading(true);
            await cotizacionesService.save(payload);
            showToast(isEditing ? 'Cotización actualizada exitosamente' : 'Cotización creada exitosamente', 'success');
            navigate('/compras');
        } catch (error: any) {
            console.error('Error saving cotizacion:', error);
            showToast(error.message || 'Error al guardar la cotización', 'error');
        } finally {
            setLoading(false);
        }
    };

    const totalGeneral = items.reduce((sum, item) => sum + item.total, 0);

    return (
        <div className="min-h-screen bg-slate-50">
            <Navbar />
            <div className="flex">
                <Sidebar />
                <main className="flex-1 p-8 pt-[105px] transition-all duration-300" style={{ marginLeft: 'var(--sidebar-width, 256px)' }}>
                    <div className="w-full">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800">
                                    {isEditing ? 'Editar Cotización' : 'Nueva Cotización'}
                                </h1>
                                <p className="text-slate-600">
                                    {formData.requisicionId ? `Basada en Requisición (ID: ${formData.requisicionNumero})` : 'Creación Manual'}
                                </p>
                            </div>
                            <button
                                onClick={() => navigate('/compras')}
                                className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                                Cancelar
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Número
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.numero}
                                        readOnly
                                        className="w-full px-4 py-2 text-sm bg-slate-100 border border-slate-300 rounded-lg text-slate-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Fecha
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.fecha}
                                        onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                                        className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Validez (días)
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.validezDias}
                                        onChange={(e) => setFormData({ ...formData, validezDias: Number(e.target.value) })}
                                        className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    {/* Provider selection removed */}
                                </div>
                                <div className="md:col-span-1">
                                    {/* Placeholder for layout balance */}
                                </div>
                                <div className="md:col-span-3">
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Observaciones
                                    </label>
                                    <textarea
                                        rows={2}
                                        value={formData.observaciones}
                                        onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                                        className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                                    ></textarea>
                                </div>
                            </div>

                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-slate-800">Items de la Cotización</h3>

                                    <div className="flex gap-2">
                                        <button

                                            type="button"
                                            onClick={handleAddItemManual}
                                            className="px-3 py-1.5 text-sm font-medium text-teal-600 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors"
                                        >
                                            + Agregar Manual
                                        </button>
                                    </div>
                                </div>

                                <div className="overflow-visible min-h-[300px]">
                                    <table className="w-full">
                                        <thead className="bg-slate-50 border-b border-slate-200">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-1/3">Principio Activo</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase w-24">Cant.</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase w-32">Precio Unit.</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase w-20">IVA %</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase w-32">Total</th>
                                                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase w-16"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200">
                                            {items.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500 italic">
                                                        No hay items agregados. Haga clic en "+ Agregar Item".
                                                    </td>
                                                </tr>
                                            ) : (
                                                items.map((item) => (
                                                    <tr key={item.id} className="relative">
                                                        <td className="p-2 align-top relative">
                                                            <input
                                                                type="text"
                                                                value={item.producto}
                                                                onChange={(e) => handleItemChange(item.id, 'producto', e.target.value)}
                                                                onFocus={() => {
                                                                    if (item.searchResults && item.searchResults.length > 0) {
                                                                        setItems(items.map(i => i.id === item.id ? { ...i, showResults: true } : i));
                                                                    }
                                                                }}
                                                                placeholder="Buscar principio activo..."
                                                                className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                                                            />
                                                            {/* Results Dropdown for this item */}
                                                            {item.showResults && (
                                                                <div className="absolute left-2 right-2 top-full mt-1 bg-white rounded-lg shadow-xl border border-slate-200 py-1 max-h-48 overflow-y-auto z-50">
                                                                    {item.searching ? (
                                                                        <div className="px-3 py-2 text-xs text-slate-500 flex items-center">
                                                                            <i className="ri-loader-4-line animate-spin mr-2"></i> Buscando...
                                                                        </div>
                                                                    ) : item.searchResults?.length === 0 ? (
                                                                        <div className="px-3 py-2 text-xs text-slate-500">No se encontraron productos</div>
                                                                    ) : (
                                                                        item.searchResults?.map((med, idx) => (
                                                                            <button
                                                                                key={med.id_sustancia ? `${med.id_sustancia}-${idx}` : `${med.id}-${idx}`}
                                                                                type="button"
                                                                                onClick={() => handleSelectProduct(item.id, med)}
                                                                                className="w-full text-left px-3 py-2 text-xs hover:bg-teal-50 flex flex-col border-b border-slate-50 last:border-0"
                                                                            >
                                                                                <span className="font-medium text-slate-800">{med.nombre_sustancia || med.nombre}</span>
                                                                            </button>
                                                                        ))
                                                                    )}
                                                                    <div
                                                                        className="fixed inset-0 z-40 bg-transparent"
                                                                        style={{ pointerEvents: 'none' }}
                                                                    ></div>
                                                                    <div className="border-t border-slate-100 p-1 flex justify-end">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setItems(items.map(i => i.id === item.id ? { ...i, showResults: false } : i))}
                                                                            className="text-xs text-slate-500 hover:text-slate-700 font-medium px-2"
                                                                        >
                                                                            Cerrar
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="p-2 align-top">
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={item.cantidad}
                                                                onChange={(e) => handleItemChange(item.id, 'cantidad', e.target.value)}
                                                                className="w-full px-2 py-1 text-sm text-right border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                                                            />
                                                        </td>
                                                        <td className="p-2 align-top">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                value={item.precioUnitario}
                                                                onChange={(e) => handleItemChange(item.id, 'precioUnitario', e.target.value)}
                                                                className="w-full px-2 py-1 text-sm text-right border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                                                            />
                                                        </td>
                                                        <td className="p-2">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                value={item.iva}
                                                                onChange={(e) => handleItemChange(item.id, 'iva', e.target.value)}
                                                                className="w-full px-2 py-1 text-sm text-right border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                                                            />
                                                        </td>
                                                        <td className="p-2 text-right text-sm font-medium text-slate-800">
                                                            ${item.total.toLocaleString()}
                                                        </td>
                                                        <td className="p-2 text-center">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveItem(item.id)}
                                                                className="text-red-500 hover:text-red-700 transition-colors"
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )))}
                                        </tbody>
                                        <tfoot className="bg-slate-50 border-t border-slate-200">
                                            <tr>
                                                <td colSpan={4} className="px-4 py-3 text-right font-bold text-slate-700">Total General:</td>
                                                <td className="px-4 py-3 text-right font-bold text-teal-600 text-lg">${totalGeneral.toLocaleString()}</td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>

                            <div className="flex justify-end pt-6 border-t border-slate-200">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors shadow-sm disabled:opacity-70 flex items-center"
                                >
                                    {loading && <i className="ri-loader-4-line animate-spin mr-2"></i>}
                                    {isEditing ? 'Guardar Cambios' : 'Crear Cotización'}
                                </button>
                            </div>
                        </form>
                    </div>
                </main>
            </div>
        </div>
    );
}
